import { pl2000ToPUWG1992 } from './coordConvert';

const NMT_API_URL = 'https://services.gugik.gov.pl/nmt/';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Pojedyncze zapytanie do GUGiK NMT API.
 * Zwraca tablicę wysokości lub rzuca błąd.
 */
async function fetchElevationsSingle(puwg92Points) {
  // Zaokrąglenie do 0.01 m (cm) — NMT ma raster 1×1 m, więc to i tak overkill.
  // Drastycznie skraca URL (16 cyfr po przecinku → 2) i pozwala wysłać większy batch bez 414.
  const list = puwg92Points.map(p => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(',');
  const url = `${NMT_API_URL}?request=GetHByPointList&list=${encodeURIComponent(list)}`;

  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 502 || response.status === 503) {
      throw new Error('Serwer GUGiK NMT jest chwilowo niedostępny (awaria).');
    }
    throw new Error(`Błąd serwera GUGiK NMT (HTTP ${response.status}).`);
  }

  const text = await response.text();

  if (text.includes('błąd') || text.includes('error')) {
    throw new Error('Serwer GUGiK NMT zwrócił błąd.');
  }

  // Odpowiedź: "x1 y1 h1,x2 y2 h2,..."
  const elevations = text.split(',').map(entry => {
    const parts = entry.trim().split(/\s+/);
    return parseFloat(parts[2]) || 0;
  });

  // Same zera = serwer przeciążony, traktuj jako błąd do retry
  const allZeros = elevations.length > 0 && elevations.every(h => h === 0);
  if (allZeros) {
    throw new Error('GUGiK zwrócił 0.0 dla wszystkich punktów.');
  }

  return elevations;
}

/**
 * Pobiera wysokości NMT z retry (3 próby, co 2s).
 * @param {Array<{coordX: number, coordY: number}>} points - punkty w PL-2000
 * @returns {Promise<{elevations: number[], source: string}>}
 */
export async function fetchElevations(points) {
  if (!points || points.length === 0) return { elevations: [], source: 'none' };

  // Konwersja PL-2000 → PUWG1992
  const puwg92Points = points.map(p => pl2000ToPUWG1992(p.coordX, p.coordY));

  let lastError = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const elevations = await fetchElevationsSingle(puwg92Points);
      if (attempt > 1) {
        console.log(`GUGiK NMT: sukces po ${attempt} próbie`);
      }
      return { elevations, source: 'gugik' };
    } catch (err) {
      lastError = err;
      console.warn(`GUGiK NMT próba ${attempt}/${MAX_RETRIES}: ${err.message}`);
      if (attempt < MAX_RETRIES) {
        await delay(RETRY_DELAY_MS);
      }
    }
  }

  // Wszystkie próby GUGiK się nie powiodły
  throw new Error(
    `Serwer GUGiK NMT niedostępny po ${MAX_RETRIES} próbach (${lastError.message}). Spróbuj ponownie później.`
  );
}

/**
 * Pobiera wysokości NMT dla pełnej siatki punktów (xCoords × yCoords) w PL-2000.
 * Batchuje zapytania (limit URL GUGiK ~8 KB), z progress callbackiem.
 *
 * @param {number[]} xCoords - tablica X w PL-2000
 * @param {number[]} yCoords - tablica Y w PL-2000 (rosnąco)
 * @param {Object} [opts]
 * @param {number} [opts.batchSize=150] - liczba punktów na batch (limit URL GUGiK ~8 KB; przy zaokrągleniu do cm punkt to ~25 znaków enkodowanych, więc 150 ≈ 3.7 KB — bezpiecznie)
 * @param {(done: number, total: number) => void} [opts.onProgress] - callback po każdym batchu
 * @param {{x:number,y:number}[]} [opts.extraPoints] - dodatkowe punkty (np. otwory) doklejone na koniec listy — zwracane jako `extraElevations`
 * @returns {Promise<{ grid: number[][], extraElevations: number[] }>} - grid[j=Y][i=X] rzędne (m n.p.m.)
 */
export async function fetchElevationGrid(xCoords, yCoords, opts = {}) {
  const { batchSize = 150, onProgress = null, extraPoints = [] } = opts;
  if (!xCoords?.length || !yCoords?.length) return { grid: [], extraElevations: [] };

  // Wszystkie punkty siatki + extra (otwory) na końcu
  const allPoints = [];
  for (let j = 0; j < yCoords.length; j++) {
    for (let i = 0; i < xCoords.length; i++) {
      allPoints.push({ coordX: xCoords[i], coordY: yCoords[j] });
    }
  }
  const gridCount = allPoints.length;
  for (const p of extraPoints) {
    allPoints.push({ coordX: p.x ?? p.coordX, coordY: p.y ?? p.coordY });
  }

  const allElevations = new Array(allPoints.length);
  const total = allPoints.length;
  let done = 0;

  for (let start = 0; start < allPoints.length; start += batchSize) {
    const batch = allPoints.slice(start, start + batchSize);
    const { elevations } = await fetchElevations(batch);
    for (let k = 0; k < elevations.length; k++) {
      allElevations[start + k] = elevations[k];
    }
    done += batch.length;
    if (onProgress) onProgress(done, total);
  }

  // Rozpakuj siatkę z pierwszych gridCount wartości
  const grid = [];
  let idx = 0;
  for (let j = 0; j < yCoords.length; j++) {
    const row = new Array(xCoords.length);
    for (let i = 0; i < xCoords.length; i++) {
      row[i] = allElevations[idx++];
    }
    grid.push(row);
  }
  // Pozostałe — extra (otwory)
  const extraElevations = allElevations.slice(gridCount);
  return { grid, extraElevations };
}

/**
 * Interpoluje punkty pośrednie w PL-2000 między dwoma otworami.
 * @param {object} bh1 - otwór z coordX, coordY
 * @param {object} bh2 - otwór z coordX, coordY
 * @param {number} n - liczba punktów pośrednich (bez końcowych)
 * @returns {Array<{coordX: number, coordY: number, t: number}>}
 */
function interpolatePoints(bh1, bh2, n) {
  const result = [];
  for (let i = 1; i <= n; i++) {
    const t = i / (n + 1);
    result.push({
      coordX: bh1.coordX + (bh2.coordX - bh1.coordX) * t,
      coordY: bh1.coordY + (bh2.coordY - bh1.coordY) * t,
      t,
    });
  }
  return result;
}

/**
 * Pobiera profil terenu NMT wzdłuż linii przekroju.
 * @param {Array} boreholes - otwory z coordX, coordY, distance
 * @param {number} numIntermediate - liczba punktów pośrednich między otworami (domyślnie 10)
 * @returns {Promise<{profile: Array<{distance: number, elevation: number}>, source: string}>}
 */
export async function fetchTerrainProfile(boreholes, numIntermediate = 10) {
  // Filtruj otwory z coordX/coordY
  const withCoords = boreholes.filter(b => b.coordX != null && b.coordY != null);
  if (withCoords.length < 2) {
    throw new Error('Potrzebne minimum 2 otwory z współrzędnymi PL-2000');
  }

  // Zbuduj listę punktów: otwory + punkty pośrednie
  const allPoints = [];   // {coordX, coordY}
  const allDistances = []; // odległość na przekroju

  for (let i = 0; i < withCoords.length; i++) {
    const bh = withCoords[i];

    // Punkt otworu
    allPoints.push({ coordX: bh.coordX, coordY: bh.coordY });
    allDistances.push(bh.distance);

    // Punkty pośrednie (oprócz ostatniego odcinka)
    if (i < withCoords.length - 1) {
      const nextBh = withCoords[i + 1];
      const intermediates = interpolatePoints(bh, nextBh, numIntermediate);
      for (const pt of intermediates) {
        allPoints.push({ coordX: pt.coordX, coordY: pt.coordY });
        // Interpolacja distance
        const dist = bh.distance + (nextBh.distance - bh.distance) * pt.t;
        allDistances.push(dist);
      }
    }
  }

  // Pobierz wysokości z API GUGiK (z retry)
  const { elevations, source } = await fetchElevations(allPoints);

  // Złóż wynik
  const profile = allDistances.map((distance, i) => ({
    distance,
    elevation: elevations[i],
  }));

  return { profile, source };
}
